import {Link, NavLink, NavLinkRenderProps, useLocation} from 'react-router'
import {useTranslation} from 'react-i18next'
import {getEnvVars} from '~/utils/env'
import {WebsitePath} from '~/utils/routes'
import {AppPath} from '~/utils/routes.app'
import {SupportedLanguage} from '~/utils/locales'
import {useLocalizedPath} from '~/utils/useLocalizedPath'
import {getWebsiteSearchParams} from '~/utils/url'

type CommonLinkProps = Omit<React.ComponentProps<typeof Link>, 'to' | 'relative' | 'className'>

type BaseTLinkProps = {
  to: WebsitePath
  queryParams?: Record<string, string>
  customLocale?: SupportedLanguage
  noRedirect?: boolean
}

type NavTLinkProps = BaseTLinkProps & {
  isNav: true
  className?: string | ((props: NavLinkRenderProps) => string | undefined) | undefined
}
type RegularTLinkProps = BaseTLinkProps & {
  isNav?: false
  className?: string
}

type TLinkProps = CommonLinkProps & (NavTLinkProps | RegularTLinkProps)
type TAppLinkProps = CommonLinkProps & {
  to: AppPath
  queryParams?: Record<string, string>
  customLocale?: SupportedLanguage
  className?: string
}

type BuildParamsOptions = {
  queryParams?: Record<string, string>
  isAppUrl?: boolean
  locale?: SupportedLanguage
  noRedirect?: boolean
}

const buildSearchParams = ({
  queryParams = {},
  isAppUrl,
  locale,
  noRedirect
}: BuildParamsOptions) => {
  const params: Record<string, string> = {...queryParams}

  if (isAppUrl && locale) {
    params.lng = locale
  } else if (noRedirect) {
    params.noRedirect = 'true'
  }

  const hasParams = Object.keys(params).length > 0
  if (!hasParams) return {searchParams: null, searchString: ''}

  return {searchParams: params, searchString: `?${new URLSearchParams(params).toString()}`}
}

export const TLink = ({
  to,
  queryParams,
  customLocale,
  noRedirect,
  isNav,
  className,
  ...props
}: TLinkProps) => {
  const localizedTo = useLocalizedPath(to, customLocale)
  const {i18n} = useTranslation()
  const {search} = useLocation()
  const {noRedirect: noRedirectFromSearch} = getWebsiteSearchParams(search) || {}

  const effectiveLocale = customLocale || (i18n.language as SupportedLanguage)
  const finalNoRedirect = noRedirect || noRedirectFromSearch

  const {searchString} = buildSearchParams({
    queryParams,
    isAppUrl: false,
    locale: effectiveLocale,
    noRedirect: finalNoRedirect
  })
  const rel = props.target === '_blank' ? 'noopener noreferrer' : undefined

  const linkData = {pathname: localizedTo, search: searchString}

  if (isNav) return <NavLink {...props} to={linkData} className={className} />
  return <Link {...props} to={linkData} rel={rel} className={className} />
}

export const TAppLink = ({to, queryParams, customLocale, ...props}: TAppLinkProps) => {
  const {i18n} = useTranslation()

  const effectiveLocale = customLocale || (i18n.language as SupportedLanguage)

  const {searchString} = buildSearchParams({
    queryParams,
    isAppUrl: true,
    locale: effectiveLocale
  })
  const rel = props.target === '_blank' ? 'noopener noreferrer' : undefined

  const href = `${getEnvVars().VITE_LARA_APP_URL}${to}${searchString}`
  return <a {...props} href={href} rel={rel} />
}
